import * as React from "react";
import styled, { useTheme, css } from "styled-components";

import { COUNTER } from "@config/testIds/counter";

import { Minus, Plus } from "@ui/Assets/MyIcons";
import { Button } from "@ui/Atoms/Button";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { rem } from "@ui/helpers";

const calcValue = (
  current: number,
  value: number,
  maxValue: number,
  minValue?: number,
): number => {
  if (!value) {
    return current;
  }

  if (value > (minValue ?? 0) && maxValue > value) {
    return value;
  }

  if (value > maxValue) {
    return maxValue;
  }

  if (typeof minValue === "number" && minValue > value) {
    return minValue;
  }

  return value;
};

interface CounterProps {
  maxValue: number;
  minValue: number;
  value: number;
  step: number;
  updates: number;
}

type Action = "decrement" | "increment";

interface Props extends CounterProps {
  testId?: string;
  className?: string;
  variant?: "fixed" | "fill";
  customSize?: string;
  customValue?: string;
  extraLabel?: string;
  onChange: (count: number, action?: Action) => void;
}

const SimplifiedCounter = (props: Props) => {
  const theme = useTheme();
  const step = props.step ? props.step : 1;
  const minValue = props.minValue ? props.minValue : 0;
  const getNewValue = (value = 0) =>
    calcValue(value, props.value, props.maxValue, props.minValue);

  const [count, setCount] = React.useState<number>(() => getNewValue());
  const [updates, setUpdates] = React.useState(() => props.updates);

  // Must be used only when component is mounted
  React.useEffect(() => {
    if (!props.value) return;
    if (count) return;

    setCount(getNewValue(count));
  }, [props.value, props.maxValue, props.minValue, count]);

  // Must be triggered when props.updates changes
  // Workaround for propagate changes from CalculateTimeModal component
  React.useEffect(() => {
    if (!props.value) return;
    if (props.updates === updates) return;

    setCount(getNewValue(count));
    setUpdates(props.updates);
  }, [
    props.value,
    props.maxValue,
    props.minValue,
    props.updates,
    count,
    updates,
  ]);

  const handleCountSet = (val: number, action?: "decrement" | "increment") => {
    setCount(val);
    props.onChange?.(val, action);
  };

  const handleDecrement = () => {
    const result = count - step;
    if (result >= minValue) {
      handleCountSet(result, "decrement");
    }
  };

  const handleIncrement = () => {
    const result = count + step;
    if (props.maxValue >= result) {
      handleCountSet(result, "increment");
    }
  };

  return (
    <TestWrapper testId={props.testId}>
      <Container className={props.className} variant={props.variant}>
        <TestWrapper testId={COUNTER.BTN_DECREMENT}>
          <CounterButton
            type="button"
            variant="borderGrey"
            size="icon"
            onClick={handleDecrement}
            disabled={count <= minValue}
          >
            <Minus width={theme.icons.sizes.base_x3} />
          </CounterButton>
        </TestWrapper>

        <Counter variant={props.variant}>
          {props.customValue ?? count}
          {!!props.extraLabel && ` ${props.extraLabel}`}
        </Counter>

        <TestWrapper testId={COUNTER.BTN_INCREMENT}>
          <CounterButton
            type="button"
            variant="borderGrey"
            size="icon"
            auto
            onClick={handleIncrement}
            disabled={count >= props.maxValue}
          >
            <Plus width={theme.icons.sizes.base_x3} />
          </CounterButton>
        </TestWrapper>
      </Container>
    </TestWrapper>
  );
};

export {
  CounterProps as SimplifiedCounterProps,
  Action as SimplifiedCounterAction,
  SimplifiedCounter,
  Counter as CounterComponent,
};

const Container = styled.div<Pick<Props, "variant">>`
  display: flex;
  align-items: center;
  text-align: center;
  user-select: none;
  gap: ${props => props.theme.margins.base_x4};

  ${props =>
    props.variant === "fill" &&
    css`
      justify-content: space-between;
    `}
`;

const CounterButton = styled(Button)<{ customSize?: string }>`
  border: 1px solid ${props => props.theme.palette.grey100};
  font-weight: ${props => props.theme.fonts.weights.light};
  font-size: ${props => props.theme.fonts.sizes.h5};
  min-height: ${props =>
    props.customSize ? props.customSize : props.theme.margins.base_x5};
  color: ${props => props.theme.palette.grey800};
  line-height: 0;
  user-select: none;
  border-radius: 50%;

  &:hover,
  &:active {
    color: ${props => props.theme.palette.grey800};
  }

  &:disabled {
    color: ${props => props.theme.palette.grey600};
  }

  ${props => props.theme.breakpoints.tablet} {
    min-width: ${props => props.customSize || props.theme.margins.base_x5};
  }
`;

const Counter = styled.div<Pick<Props, "variant">>`
  margin: 0;
  font-weight: ${props => props.theme.fonts.weights.semibold};
  font-size: ${props => props.theme.fonts.baseSize};
  display: flex;
  justify-content: center;
  width: ${rem(88)};

  ${props =>
    props.variant === "fill" &&
    css`
      width: auto;
    `}
`;
